// src/pages/ProductDetail.js
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useData } from "../context/DataContext";
import "./ProductDetail.css";

export default function ProductDetail() {
  const { slug } = useParams();
  const { products, niches, loading } = useData();
  const [selectedVariant, setSelectedVariant] = useState(0);

  const product = products.find((p) => p.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) {
    return (
      <div className="product-detail product-detail--loading">
        <div className="container">
          <div className="pd-skeleton" />
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="product-detail product-detail--404">
        <div className="container">
          <h2>Product not found</h2>
          <Link to="/products" className="btn btn-primary">← Back to Products</Link>
        </div>
      </div>
    );
  }

  const productNiches = niches.filter((n) => product.niches?.includes(n.id));
  const enquirySubject = encodeURIComponent(`Enquiry: ${product.name} – ${product.variants?.[selectedVariant]?.size || ""}`);
  const enquiryBody = encodeURIComponent(`Hi,\n\nI am interested in:\nProduct: ${product.name}\nSize: ${product.variants?.[selectedVariant]?.size || "Please advise"}\n\nPlease share pricing and availability.\n\nThank you.`);

  return (
    <div className="product-detail">
      {/* Breadcrumb */}
      <div className="pd-breadcrumb container">
        <Link to="/">Home</Link>
        <span>›</span>
        <Link to="/products">Products</Link>
        <span>›</span>
        <span>{product.name}</span>
      </div>

      <div className="container">
        <div className="pd-main">
          {/* Left – Image */}
          <div className="pd-image-col">
            <div className="pd-image-wrap">
              {product.imageUrl ? (
                <img src={product.imageUrl} alt={product.name} className="pd-image" />
              ) : (
                <div className="pd-image-placeholder">
                  <span>🌿</span>
                  <p>{product.name}</p>
                </div>
              )}
              {product.featured && (
                <div className="pd-featured-badge">⭐ Featured Product</div>
              )}
            </div>

            {/* Badges */}
            <div className="pd-badges">
              {product.badges?.map((b) => (
                <span key={b} className="badge">{b}</span>
              ))}
            </div>
          </div>

          {/* Right – Info */}
          <div className="pd-info-col">
            <p className="pd-category">{product.category}</p>
            <h1 className="pd-title">{product.name}</h1>
            <p className="pd-tagline">{product.tagline}</p>

            <p className="pd-description">{product.description}</p>

            {/* Niche Tags */}
            {productNiches.length > 0 && (
              <div className="pd-niches">
                <span className="pd-section-label">Suitable for:</span>
                <div className="pd-niche-tags">
                  {productNiches.map((n) => (
                    <span key={n.id} className="pd-niche-tag">
                      {n.icon} {n.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Variant Selector */}
            {product.variants?.length > 0 && (
              <div className="pd-variants">
                <span className="pd-section-label">Select Pack Size:</span>
                <div className="pd-variant-pills">
                  {product.variants.map((v, i) => (
                    <button
                      key={v.sku}
                      className={`pd-variant-pill ${selectedVariant === i ? "pd-variant-pill--active" : ""}`}
                      onClick={() => setSelectedVariant(i)}
                    >
                      {v.size}
                      {v.price && <span className="pd-variant-price">₹{v.price}</span>}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="pd-actions">
              <a
                href={`mailto:${product.enquiryEmail || "sales@sunaharco.com"}?subject=${enquirySubject}&body=${enquiryBody}`}
                className="btn btn-primary pd-enquire"
              >
                📧 Enquire Now
              </a>
              <a
                href={`https://wa.me/91XXXXXXXXXX?text=${encodeURIComponent(`Hi! I'm interested in ${product.name} – ${product.variants?.[selectedVariant]?.size || ""}. Please share details.`)}`}
                className="btn btn-gold"
                target="_blank"
                rel="noreferrer"
              >
                💬 WhatsApp
              </a>
            </div>

            <p className="pd-email-note">
              Or email us at{" "}
              <a href={`mailto:${product.enquiryEmail || "sales@sunaharco.com"}`}>
                {product.enquiryEmail || "sales@sunaharco.com"}
              </a>
            </p>
          </div>
        </div>

        {/* Details Tabs */}
        <div className="pd-details">
          {/* Benefits */}
          {product.benefits?.length > 0 && (
            <div className="pd-section">
              <h3 className="pd-section-title">Key Benefits</h3>
              <div className="pd-benefits-grid">
                {product.benefits.map((b) => (
                  <div key={b} className="pd-benefit">
                    <span className="pd-benefit__check">✓</span>
                    <span>{b}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* How to Use */}
          {product.howToUse && (
            <div className="pd-section">
              <h3 className="pd-section-title">How to Use</h3>
              <div className="pd-how-box">
                <span className="pd-how-icon">📋</span>
                <p>{product.howToUse}</p>
              </div>
            </div>
          )}

          {/* Usage Suitability */}
          {product.usageSuitability?.length > 0 && (
            <div className="pd-section">
              <h3 className="pd-section-title">Best Suited For</h3>
              <div className="pd-uses-grid">
                {product.usageSuitability.map((u) => (
                  <div key={u} className="pd-use-card">
                    <span>{USE_ICONS[u] || "🌱"}</span>
                    <span>{u}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const USE_ICONS = {
  Agriculture: "🚜",
  "Home gardening": "🏡",
  Nurseries: "🪴",
  Landscaping: "🌳",
};
